/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { speak } from "../lib/i18n";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Minus, Plus, Scale, Droplets, AlertCircle, CheckCircle2, Volume2 } from "lucide-react";
import { MealPhotos } from "./MealPhotos";
import { MealScoreCard } from "./MealScore";

const MGDL = 18;

export function classifyWeight(v, patient) {
  const cible = patient?.cible_poids;
  if (!cible) return { key: "none", label: "Pas encore de cible", color: "#0A1426" };
  const marge = patient?.cible_poids_marge ?? 3;
  const diff = Math.abs(v - cible);
  if (diff <= marge) return { key: "in", label: "Dans la cible", color: "#5CB85C" };
  if (diff <= marge * 2) return { key: "watch", label: v > cible ? "Un peu au-dessus" : "Un peu en dessous", color: "#D4AF5A" };
  return { key: "high", label: v > cible ? "Au-dessus de la cible" : "En dessous de la cible", color: "#D9534F" };
}

export function classifyGlucose(mgdl, patient) {
  const min = patient?.cible_glycemie_min, max = patient?.cible_glycemie_max;
  if (!min && !max) return { key: "none", label: "Pas encore de cible", color: "#0A1426" };
  if (min && mgdl < min) return { key: "low", label: "Trop basse", color: "#D9534F" };
  if (max && mgdl > max) return { key: "high", label: "Élevée", color: "#D9534F" };
  if (max && mgdl > max - 15) return { key: "watch", label: "À surveiller", color: "#D4AF5A" };
  return { key: "in", label: "Dans la cible", color: "#5CB85C" };
}

function StepCounter({ value, setValue, min, max, step, decimals, testid, unit }) {
  const fmt = (v) => v.toFixed(decimals);
  return (
    <div className="flex items-center justify-center gap-3">
      <button data-testid={`${testid}-minus`} onClick={() => setValue(Math.max(min, +(value - step).toFixed(decimals)))}
        className="w-14 h-14 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"><Minus size={24} /></button>
      <div className="text-center min-w-[130px]">
        <div data-testid={testid} className="font-[Fraunces] text-5xl font-bold text-[#0A1426] leading-none">{fmt(value)}</div>
        <div className="text-[12px] text-[#0A1426]/60 font-bold mt-1">{unit}</div>
      </div>
      <button data-testid={`${testid}-plus`} onClick={() => setValue(Math.min(max, +(value + step).toFixed(decimals)))}
        className="w-14 h-14 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"><Plus size={24} /></button>
    </div>
  );
}

function Badge({ cls, testid }) {
  return (
    <div data-testid={testid} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold" style={{ backgroundColor: `${cls.color}1f`, color: cls.color }}>
      {cls.key === "in" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}{cls.label}
    </div>
  );
}

function Sparkline({ points, color, target }) {
  if (points.length < 2) return null;
  const vals = points.map((p) => p.v).concat(target ? [target] : []);
  const max = Math.max(...vals), min = Math.min(...vals), span = Math.max(1e-6, max - min);
  const w = points.length * 36;
  const y = (v) => 70 - ((v - min) / span) * 54 - 8;
  return (
    <svg viewBox={`0 0 ${w} 80`} className="w-full h-24">
      {target && <line x1="0" x2={w} y1={y(target)} y2={y(target)} stroke="#D9534F" strokeDasharray="4 4" strokeWidth="1.5" />}
      <polyline points={points.map((p, i) => `${i * 36 + 10},${y(p.v)}`).join(" ")} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => <circle key={i} cx={i * 36 + 10} cy={y(p.v)} r="3.5" fill={color} />)}
    </svg>
  );
}

export function VitalsTab() {
  const { patient, setPatient, lang } = useApp();
  const [unit, setUnit] = useState(() => localStorage.getItem("bc_glyc_unit") || "mg/dL");
  const [poids, setPoids] = useState(70);
  const [glyc, setGlyc] = useState(110);
  const [moment, setMoment] = useState("jeun");
  const [items, setItems] = useState([]);

  useEffect(() => { localStorage.setItem("bc_glyc_unit", unit); }, [unit]);

  const load = () => {
    if (!patient) return;
    api.get(`/vitals?patient_id=${patient.id}`).then(({ data }) => {
      setItems(data);
      const lastP = data.find((x) => x.type === "poids"); if (lastP) setPoids(lastP.valeur);
      localStorage.setItem("bc_vitals", JSON.stringify(data));
    }).catch(() => { try { setItems(JSON.parse(localStorage.getItem("bc_vitals") || "[]")); } catch { /* ignore */ } });
    api.get(`/patients/${patient.id}`).then(({ data }) => setPatient(data)).catch(() => null);
  };
  useEffect(load, [patient?.id]); // eslint-disable-line

  const glycMgdl = unit === "mmol/L" ? glyc * MGDL : glyc;
  const clsP = classifyWeight(poids, patient);
  const clsG = classifyGlucose(glycMgdl, patient);

  const switchUnit = (u) => {
    if (u === unit) return;
    setGlyc(u === "mmol/L" ? +(glyc / MGDL).toFixed(1) : Math.round(glyc * MGDL));
    setUnit(u);
  };

  const save = async (type) => {
    const body = type === "poids"
      ? { patient_id: patient.id, type, valeur: poids, unite: "kg", client_datetime: new Date().toISOString() }
      : { patient_id: patient.id, type, valeur: glyc, unite: unit, moment, client_datetime: new Date().toISOString() };
    const r = await postOrQueue("/vitals", body);
    const cls = type === "poids" ? clsP : clsG;
    if (r._queued) toast.info("Enregistré hors-ligne. Sera envoyé au retour du réseau.");
    else toast.success(type === "poids" ? "Poids enregistré" : "Glycémie enregistrée");
    speak(`${type === "poids" ? "Poids" : "Glycémie"} ${type === "poids" ? poids : glyc} ${type === "poids" ? "kilos" : unit}. ${cls.key === "none" ? "" : cls.label}.`, lang);
    load();
  };

  const histP = useMemo(() => items.filter((x) => x.type === "poids").slice(0, 10).reverse().map((x) => ({ v: x.valeur, d: x.datetime })), [items]);
  const histG = useMemo(() => items.filter((x) => x.type === "glycemie").slice(0, 10).reverse().map((x) => ({ v: unit === "mmol/L" ? +(x.valeur_mgdl / MGDL).toFixed(1) : x.valeur_mgdl, d: x.datetime })), [items, unit]);
  const tgtG = patient?.cible_glycemie_max ? (unit === "mmol/L" ? +(patient.cible_glycemie_max / MGDL).toFixed(1) : patient.cible_glycemie_max) : null;
  const fmtD = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });

  return (
    <div className="flex flex-col gap-4 pt-2" data-testid="vitals-tab">
      <section className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
        <div className="flex items-center gap-2 mb-3"><Scale size={20} className="text-[#2BA3A0]" /><h3 className="font-[Fraunces] text-xl font-bold text-[#0A1426]">Mon poids</h3></div>
        <StepCounter value={poids} setValue={setPoids} min={25} max={250} step={0.5} decimals={1} testid="vital-poids" unit="kg" />
        <div className="mt-4"><Badge cls={clsP} testid="poids-classification" /></div>
        <p className="text-[11px] text-[#0A1426]/60 mt-3 leading-snug">
          {patient?.cible_poids ? <>Cible fixée par votre médecin : {patient.cible_poids} kg (± {patient.cible_poids_marge ?? 3} kg).</> : <>Votre praticien pourra fixer une cible de poids.</>} Ceci n'est pas un avis médical.
        </p>
        <Button data-testid="poids-save-btn" onClick={() => save("poids")} className="w-full h-14 mt-4 rounded-full bg-[#2BA3A0] hover:bg-[#0A1426] text-white font-bold text-base">Enregistrer mon poids</Button>
        {histP.length > 1 && (
          <div className="mt-4">
            <Sparkline points={histP} color="#2BA3A0" target={patient?.cible_poids} />
            <div className="flex justify-between text-[10px] text-[#0A1426]/60"><span>{fmtD(histP[0].d)}</span><span>Tendance du poids</span><span>{fmtD(histP[histP.length - 1].d)}</span></div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Droplets size={20} className="text-[#D9534F]" /><h3 className="font-[Fraunces] text-xl font-bold text-[#0A1426]">Ma glycémie</h3></div>
          <div className="flex bg-[#F5F3EE] rounded-full p-0.5">
            {["mg/dL", "mmol/L"].map((u) => (
              <button key={u} data-testid={`glyc-unit-${u === "mg/dL" ? "mgdl" : "mmol"}`} onClick={() => switchUnit(u)}
                className={`px-3 h-8 rounded-full text-[11px] font-bold ${unit === u ? "bg-[#0A1426] text-white" : "text-[#0A1426]"}`}>{u}</button>
            ))}
          </div>
        </div>
        {unit === "mmol/L"
          ? <StepCounter value={glyc} setValue={setGlyc} min={2} max={30} step={0.1} decimals={1} testid="vital-glyc" unit="mmol/L" />
          : <StepCounter value={glyc} setValue={setGlyc} min={40} max={500} step={1} decimals={0} testid="vital-glyc" unit="mg/dL" />}
        <div className="flex gap-2 mt-4">
          {[["jeun", "À jeun"], ["apres_repas", "Après repas"], ["autre", "Autre"]].map(([k, l]) => (
            <button key={k} data-testid={`glyc-moment-${k}`} onClick={() => setMoment(k)}
              className={`flex-1 h-11 rounded-full text-[13px] font-bold ${moment === k ? "bg-[#2BA3A0] text-white" : "bg-[#F5F3EE] text-[#0A1426]"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-4"><Badge cls={clsG} testid="glyc-classification" /></div>
        <p className="text-[11px] text-[#0A1426]/60 mt-3 leading-snug">
          {patient?.cible_glycemie_min || patient?.cible_glycemie_max
            ? <>Cible fixée par votre médecin : {patient.cible_glycemie_min ?? "—"} à {patient.cible_glycemie_max ?? "—"} mg/dL{unit === "mmol/L" && patient.cible_glycemie_max ? ` (≈ ${(patient.cible_glycemie_min / MGDL).toFixed(1)}–${(patient.cible_glycemie_max / MGDL).toFixed(1)} mmol/L)` : ""}.</>
            : <>Votre praticien pourra fixer une cible de glycémie.</>} En cas de malaise, contactez votre soignant.
        </p>
        <Button data-testid="glyc-save-btn" onClick={() => save("glycemie")} className="w-full h-14 mt-4 rounded-full bg-[#2BA3A0] hover:bg-[#0A1426] text-white font-bold text-base">Enregistrer ma glycémie</Button>
        {histG.length > 1 && (
          <div className="mt-4">
            <Sparkline points={histG} color="#D9534F" target={tgtG} />
            <div className="flex justify-between text-[10px] text-[#0A1426]/60"><span>{fmtD(histG[0].d)}</span><span>Tendance glycémie ({unit})</span><span>{fmtD(histG[histG.length - 1].d)}</span></div>
          </div>
        )}
      </section>

      <MealPhotos glycemieMgdl={items.find((x) => x.type === "glycemie")?.valeur_mgdl} />
      {patient && <MealScoreCard patientId={patient.id} compact />}

      {items.length > 0 && (
        <section className="bg-white rounded-3xl border border-[#E4E0D5] p-5" data-testid="vitals-history">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Historique</div>
            <button data-testid="vitals-speak-btn" onClick={() => speak(items.slice(0, 3).map((x) => `${x.type === "poids" ? "Poids" : "Glycémie"} ${x.valeur} ${x.type === "poids" ? "kilos" : x.unite} le ${fmtD(x.datetime)}`).join(". "), lang)}
              className="w-9 h-9 rounded-full bg-[#F5F3EE] text-[#0A1426] grid place-items-center"><Volume2 size={16} /></button>
          </div>
          {items.slice(0, 8).map((x) => {
            const cls = x.type === "poids" ? classifyWeight(x.valeur, patient) : classifyGlucose(x.valeur_mgdl, patient);
            return (
              <div key={x.id} data-testid={`vital-row-${x.type}`} className="flex items-center justify-between py-2 border-b border-[#F5F3EE] last:border-0 text-[14px] text-[#0A1426]">
                <span className="flex items-center gap-2">{x.type === "poids" ? <Scale size={16} className="text-[#2BA3A0]" /> : <Droplets size={16} className="text-[#D9534F]" />}{fmtD(x.datetime)}{x.moment ? <span className="text-[11px] text-[#0A1426]/60">· {x.moment === "jeun" ? "à jeun" : x.moment === "apres_repas" ? "après repas" : ""}</span> : null}</span>
                <span className="font-bold flex items-center gap-2">{x.valeur} {x.unite}<span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cls.color }} /></span>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

const TargetField = ({ form, setForm, k, label, step = 1 }) => (
  <label className="text-[12px] text-[#0A1426]/70 flex flex-col gap-1">{label}
    <input data-testid={`target-input-${k}`} type="number" step={step} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      className="h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] font-bold" />
  </label>
);

/** Formulaire praticien : cibles poids / glycémie + dernières valeurs. */
export function VitalsTargetsForm({ patientId }) {
  const [form, setForm] = useState({ cible_poids: "", cible_poids_marge: 3, cible_glycemie_min: "", cible_glycemie_max: "" });
  const [latest, setLatest] = useState({ poids: null, glyc: null });
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get(`/patients/${patientId}`).then(({ data: p }) => setForm({
      cible_poids: p.cible_poids ?? "", cible_poids_marge: p.cible_poids_marge ?? 3,
      cible_glycemie_min: p.cible_glycemie_min ?? "", cible_glycemie_max: p.cible_glycemie_max ?? "" })).catch(() => null);
    api.get(`/vitals?patient_id=${patientId}`).then(({ data }) => setLatest({ poids: data.find((x) => x.type === "poids"), glyc: data.find((x) => x.type === "glycemie") })).catch(() => null);
  };
  useEffect(load, [patientId]); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/patients/${patientId}/vitals-targets`, {
        cible_poids: form.cible_poids === "" ? null : Number(form.cible_poids), cible_poids_marge: Number(form.cible_poids_marge),
        cible_glycemie_min: form.cible_glycemie_min === "" ? null : Number(form.cible_glycemie_min),
        cible_glycemie_max: form.cible_glycemie_max === "" ? null : Number(form.cible_glycemie_max) });
      toast.success("Cibles enregistrées — visibles dans Ma santé du patient"); load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Enregistrement impossible"); }
    setBusy(false);
  };
  const fp = { form, setForm };
  return (
    <div data-testid="vitals-targets-form" className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Poids & glycémie — cibles</h3>
        <span data-testid="vitals-latest" className="text-[11px] text-[#0A1426]/70">
          {latest.poids ? `${latest.poids.valeur} kg` : "— kg"} · {latest.glyc ? `${latest.glyc.valeur_mgdl} mg/dL` : "— mg/dL"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <TargetField {...fp} k="cible_poids" label="Poids cible (kg)" step={0.5} />
        <TargetField {...fp} k="cible_poids_marge" label="Marge (± kg)" step={0.5} />
        <TargetField {...fp} k="cible_glycemie_min" label="Glycémie min (mg/dL)" />
        <TargetField {...fp} k="cible_glycemie_max" label="Glycémie max (mg/dL)" />
      </div>
      <Button data-testid="targets-save-btn" disabled={busy} onClick={save} className="w-full h-11 mt-3 rounded-full bg-[#0A1426] text-white font-bold">Enregistrer les cibles</Button>
    </div>
  );
}
